/**
 * Session-scoped, in-memory cache of the last data each page displayed.
 *
 * Navigating back (or forward) in an SPA remounts the destination page from
 * scratch, so pages that fetch on mount would show loading skeletons on every
 * back navigation even though the user just saw the content. Pages seed their
 * state from this cache to render the last-known content on the first paint,
 * then refresh from the API in the background (stale-while-revalidate).
 *
 * Deliberately memory-only: it lives for one browser session, is never
 * persisted, and is cleared on sign-out, so entries can hold raw API
 * responses without staleness or cross-user concerns. Keys must include the
 * owning user's uid for per-user data (e.g. `cookbook:${uid}`) so an
 * account switch can never read another account's entries.
 */

const cache = new Map<string, unknown>();
const listeners = new Map<string, Set<(value: unknown) => void>>();

/** Read a cached entry. A null/undefined key (e.g. auth not resolved yet) reads nothing. */
export const getPageCache = <T>(key: string | null | undefined): T | undefined =>
  key ? (cache.get(key) as T | undefined) : undefined;

/** Store an entry and notify that key's subscribers. A null/undefined key stores nothing. */
export const setPageCache = (key: string | null | undefined, value: unknown): void => {
  if (key) {
    cache.set(key, value);
    listeners.get(key)?.forEach((listener) => listener(value));
  }
};

/**
 * Subscribe to writes of one cache key; returns an unsubscribe function.
 *
 * This is what keeps a page current when a *different* surface updates the
 * data it displays — e.g. a "meal plan deleted" undo tapped while the
 * dashboard is mounted: whichever code refetches the plans writes the cache,
 * and the mounted dashboard hears about it here instead of showing its
 * seeded (stale) copy until the next remount. Callbacks fire synchronously
 * on write; a subscriber's own setPageCache call re-enters its callback, so
 * write handlers must not write the same key.
 */
export const subscribePageCache = <T>(
  key: string | null | undefined,
  listener: (value: T) => void
): (() => void) => {
  if (!key) return () => {};
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(listener as (value: unknown) => void);
  return () => {
    set!.delete(listener as (value: unknown) => void);
    if (set!.size === 0) {
      listeners.delete(key);
    }
  };
};

/** Drop every entry — called on sign-out so nothing outlives the session it belongs to. */
export const clearPageCache = (): void => {
  cache.clear();
};
